import { createFileRoute, Link } from "@tanstack/react-router";
import { Banknote, CheckCircle2, CreditCard, FileText, ShieldCheck } from "lucide-react";

import { CarGrid } from "@/components/site/CarGrid";
import { Button } from "@/components/ui/button";
import { cars } from "@/data/cars";
import { hreflangLinks } from "@/lib/i18n";
import { publicPageHeaders } from "@/lib/http-cache";

const CANONICAL = "https://carentals.ro/ro/inchirieri-auto-fara-garantie";
const title = "Închirieri auto fără garanție București | carentals.ro";
const description =
  "Rent a car București fără garanție și fără card de credit: nicio sumă blocată, asigurare și rovinietă incluse, predare gratuită în oraș și la Otopeni. De la 44 €/zi.";

export const Route = createFileRoute("/ro/inchirieri-auto-fara-garantie")({
  headers: publicPageHeaders,
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ro_RO" },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/car-rental-no-deposit-bucharest", "/ro/inchirieri-auto-fara-garantie"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Închirieri auto fără garanție în București",
          serviceType: "Închirieri auto fără garanție",
          areaServed: { "@type": "City", name: "București", addressCountry: "RO" },
          provider: { "@id": "https://carentals.ro/#organization" },
          url: CANONICAL,
          offers: {
            "@type": "Offer",
            price: "44",
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Acasă", item: "https://carentals.ro/ro" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Închirieri auto fără garanție",
              item: CANONICAL,
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const pasi = [
  {
    icon: FileText,
    title: "1. Alegi mașina și datele",
    text: "Selectezi modelul din flotă, perioada și punctul de predare. Îți confirmăm disponibilitatea în câteva minute.",
  },
  {
    icon: CreditCard,
    title: "2. Fără blocare pe card",
    text: "Nu cerem card de credit și nu blocăm nicio garanție. Plătești cu card de debit, numerar sau transfer bancar.",
  },
  {
    icon: CheckCircle2,
    title: "3. Primești mașina",
    text: "Predare gratuită la birou sau la Otopeni, cu plinul verificat, rovinieta activă și asigurarea inclusă.",
  },
];

function Page() {
  return (
    <>
      <section className="bg-ink py-20 text-ink-foreground">
        <div className="container-page">
          <p className="eyebrow">Garanție 0 € · Fără card de credit</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
            Închirieri auto fără garanție în București
          </h1>
          <p className="mt-5 max-w-2xl text-base text-ink-foreground/80">
            Închiriezi o mașină în București fără să blochezi bani pe card. Nu cerem garanție, nu
            cerem card de credit și nu percepem taxe ascunse — tariful zilnic include asigurarea
            completă, rovinieta, kilometrajul nelimitat în România și asistența rutieră 24/7.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/rentals">Rezervă o mașină</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-ink-foreground hover:bg-white/10 hover:text-ink-foreground"
            >
              <a href="https://wa.me/40738792499" target="_blank" rel="noreferrer">
                Întreabă pe WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container-page py-20">
        <h2 className="text-3xl font-bold sm:text-4xl">Cum funcționează</h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {pasi.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-7 shadow-card">
              <p.icon className="size-6 text-gold" />
              <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page pb-20">
        <div className="grid gap-6 rounded-2xl border border-border bg-card p-8 shadow-card md:grid-cols-2">
          <div>
            <ShieldCheck className="size-6 text-gold" />
            <h2 className="mt-3 font-display text-2xl font-bold">Ce acte îți trebuie</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>• Permis de conducere valabil de cel puțin 1 an</li>
              <li>• Carte de identitate sau pașaport</li>
              <li>• Vârsta minimă 21 de ani</li>
              <li>• Card de debit, numerar (EUR/RON) sau transfer bancar</li>
            </ul>
          </div>
          <div>
            <Banknote className="size-6 text-gold" />
            <h2 className="mt-3 font-display text-2xl font-bold">Ce include tariful</h2>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>• Asigurare RCA și CASCO</li>
              <li>• Rovinieta pentru România</li>
              <li>• Kilometraj nelimitat în țară</li>
              <li>• Asistență rutieră 24/7 și predare gratuită la Otopeni</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-secondary/60 py-20">
        <div className="container-page">
          <h2 className="text-3xl font-bold sm:text-4xl">Mașini disponibile fără garanție</h2>
          <CarGrid cars={cars.slice(0, 6)} className="mt-10" />
          <div className="mt-8">
            <Button asChild variant="outline">
              <Link to="/rentals">Vezi toată flota</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
